// src/pages/estoque/PedidosMaterialPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useIsMobile } from "../../hooks/useIsMobile";

import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

function criarItemVazio() {
  return {
    modelo: "",
    quantidade: 1,
    valorUnitario: 0,
    controleEstoque: false,
  };
}

function calcularValorTotal(item) {
  const q = Number(item.quantidade) || 0;
  const vu = Number(item.valorUnitario) || 0;
  return q * vu;
}


function PedidosMaterialPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { osId } = useParams();

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [cabecalho, setCabecalho] = useState({
    origem: osId ? "OS" : "Manual",
    osId: null,
    osCodigo: "",
    empresaId: "",
    empresaNome: "",
    setorId: "",
    setorNome: "",
    maquinaId: "",
    maquinaNome: "",
  });

  const [itens, setItens] = useState([criarItemVazio()]);
  const [requisicaoNumero, setRequisicaoNumero] = useState("");
  const [salvando, setSalvando] = useState(false);
  const podeControleEstoque = cabecalho.origem !== "OS";

  useEffect(() => {
    async function carregarOsSeNecessario() {
      if (!osId) {
        // Preenche empresa a partir do contexto quando for manual (sem vínculo com OS)
        setCabecalho((prev) => ({
          ...prev,
          origem: "Manual",
          osId: null,
          osCodigo: "",
          empresaId: !modoGlobal && empresaAtual ? empresaAtual.id : "",
          empresaNome: !modoGlobal && empresaAtual ? empresaAtual.nome : "",
        }));
        return;
      }

      if (!usuarioLogado?.grupoId) return;

      setCarregando(true);
      setErro("");
      try {
        const ref = doc(db, "ordensServico", osId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErro("OS não encontrada para vincular ao pedido de material.");
          return;
        }
        const dados = snap.data();
        if (dados.grupoId !== usuarioLogado.grupoId) {
          setErro("Você não tem permissão para usar esta OS.");
          return;
        }

        setCabecalho({
          origem: "OS",
          osId: snap.id,
          osCodigo: dados.codigo || dados.id || snap.id,
          empresaId: dados.empresaId || "",
          empresaNome: dados.empresaNome || "",
          setorId: dados.setorId || "",
          setorNome: dados.setorNome || "",
          maquinaId: dados.maquinaId || "",
          maquinaNome: dados.maquinaNome || "",
        });
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar OS vinculada.");
      } finally {
        setCarregando(false);
      }
    }

    carregarOsSeNecessario();
  }, [osId, usuarioLogado, empresaAtual, modoGlobal]);

  const tituloPagina = useMemo(() => {
    if (cabecalho.origem === "OS" && cabecalho.osCodigo) {
      return `Pedido de Material - ${cabecalho.osCodigo}`;
    }
    return "Pedido de Material";
  }, [cabecalho]);

  function atualizarItem(index, campo, valor) {
    setItens((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [campo]: campo === "quantidade" || campo === "valorUnitario" ? valor.replace(",", ".") : valor,
            }
          : item
      )
    );
  }

  function adicionarItem() {
    setItens((prev) => [...prev, criarItemVazio()]);
  }

  function removerItem(index) {
    setItens((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (!usuarioLogado?.grupoId) {
      setErro("Usuário sem grupo definido.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || null;
    const empresaId =
      cabecalho.origem === "OS"
        ? cabecalho.empresaId || null
        : (!modoGlobal && empresaAtual ? empresaAtual.id : null);
    const empresaNome =
      cabecalho.origem === "OS"
        ? cabecalho.empresaNome || null
        : (!modoGlobal && empresaAtual ? empresaAtual.nome : null);

    const itensValidos = itens.filter(
      (item) =>
        String(item.modelo || "").trim() !== "" &&
        Number(item.quantidade) > 0
    );

    if (itensValidos.length === 0) {
      setErro("Informe pelo menos um material com modelo e quantidade.");
      return;
    }

    const reqNum = String(requisicaoNumero || "").trim();
    if (!reqNum) {
      setErro("Informe o número da requisição.");
      return;
    }

    if (cabecalho.origem === "Manual") {
      const algumSemControleEstoque = itensValidos.some(
        (item) => item.controleEstoque !== true && item.controleEstoque !== false
      );
      // No fluxo manual, campo é obrigatório visualmente; aqui apenas garantimos
      if (algumSemControleEstoque) {
        setErro("Defina se cada material será ou não controle de estoque.");
        return;
      }
    }

    setErro("");
    setSucesso("");
    setSalvando(true);

    try {
      const colecao = collection(db, "pedidosMateriais");

      for (const item of itensValidos) {
        const valorTotal = calcularValorTotal(item);

        await addDoc(colecao, {
          grupoId,
          grupoNome,
          empresaId,
          empresaNome,
          setorId: cabecalho.setorId || null,
          setorNome: cabecalho.setorNome || null,
          maquinaId: cabecalho.maquinaId || null,
          maquinaNome: cabecalho.maquinaNome || null,

          osId: cabecalho.origem === "OS" ? cabecalho.osId : null,
          osCodigo:
            cabecalho.origem === "OS" ? cabecalho.osCodigo || null : null,
          origem: cabecalho.origem, // "OS" ou "Manual"
          requisicaoNumero: reqNum,

          dataPedido: serverTimestamp(),
          dataRecebimento: null,
          dataUltimoRecebimento: null,

          modelo: String(item.modelo || "").trim(),
          quantidade: Number(item.quantidade) || 0,
          quantidadeRecebida: 0,
          valorUnitario: Number(item.valorUnitario) || 0,
          valorTotal,

          controleEstoque:
            cabecalho.origem === "OS" ? false : !!item.controleEstoque,

          statusPedido: "Pendente",
        });
      }

      // Se o pedido veio de uma OS, ao salvar o pedido colocamos a OS em "Aguardando Material"
      if (cabecalho.origem === "OS" && cabecalho.osId) {
        try {
          const osRef = doc(db, "ordensServico", cabecalho.osId);
          await updateDoc(osRef, {
            statusId: "fab_aguard_mat",
            statusNome: "Aguardando Material",
            statusCor: "#ca8a04",
            atualizadoEm: serverTimestamp(),
          });
        } catch (e) {
          console.error("Erro ao atualizar status da OS para Aguardando Material:", e);
        }
      }

      setSucesso("Pedido de material salvo com sucesso.");
      setItens([criarItemVazio()]);
      setRequisicaoNumero("");

      if (cabecalho.origem === "OS") {
        // Se veio de OS, volta para a lista de atendimentos
        navigate("/gerenciar/tabela-atendimentos");
      }
    } catch (e) {
      console.error(e);
      setErro("Erro ao salvar pedido de material. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  const conteudoForm = (
    <form onSubmit={handleSalvar} className="pc-form">
      {/* Cabeçalho */}
      <section className="pc-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 8, fontSize: "1rem", fontWeight: 600 }}>
          {cabecalho.origem === "OS" ? "Dados da OS" : "Dados do Pedido"}
        </h2>

        {cabecalho.origem === "OS" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            <div>
              <label className="pc-label">Nº Requisição</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  className="pc-input"
                  value={requisicaoNumero}
                  onChange={(e) => setRequisicaoNumero(e.target.value)}
                  placeholder="REQ-..."
                  required
                />
              </div>
            </div>
            <div>
              <label className="pc-label">Número da OS</label>
              <div className="pc-input" style={{ backgroundColor: "#0f172a", opacity: 0.9 }}>
                {cabecalho.osCodigo || "-"}
              </div>
            </div>
            <div>
              <label className="pc-label">Empresa</label>
              <div className="pc-input" style={{ backgroundColor: "#0f172a", opacity: 0.9 }}>
                {cabecalho.empresaNome || "-"}
              </div>
            </div>
            <div>
              <label className="pc-label">Setor</label>
              <div className="pc-input" style={{ backgroundColor: "#0f172a", opacity: 0.9 }}>
                {cabecalho.setorNome || "-"}
              </div>
            </div>
            <div>
              <label className="pc-label">Máquina</label>
              <div className="pc-input" style={{ backgroundColor: "#0f172a", opacity: 0.9 }}>
                {cabecalho.maquinaNome || "-"}
              </div>
            </div>
          </div>
        ) : (
          <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
            <div style={{ minWidth: 220, flex: 1 }}>
              <label className="pc-label">Nº Requisição</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  className="pc-input"
                  value={requisicaoNumero}
                  onChange={(e) => setRequisicaoNumero(e.target.value)}
                  placeholder="REQ-..."
                  required
                />
              </div>
            </div>
          </div>
          <p style={{ fontSize: 12, color: "#9ca3af" }}>

            Este pedido não está vinculado a nenhuma OS específica. Os materiais podem alimentar o estoque interno,
            conforme marcado abaixo.
          </p>
          </>
        )}
      </section>

      {/* Materiais */}
      <section className="pc-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <h2 style={{ fontSize: "1rem", fontWeight: 600 }}>Materiais</h2>
          <button
            type="button"
            className="pc-btn-secundario"
            onClick={adicionarItem}
          >
            + Adicionar material
          </button>
        </div>

        {itens.map((item, index) => (
          <div
            key={index}
            style={{
              border: "1px solid rgba(148, 163, 184, 0.4)",
              borderRadius: 12,
              padding: 12,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr repeat(3, 1fr)",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div>
                <label className="pc-label">Modelo / Descrição</label>
                <textarea
                  className="pc-input"
                  value={item.modelo}
                  onChange={(e) =>
                    atualizarItem(index, "modelo", e.target.value)
                  }
                  placeholder="Ex: Mangueira 1/4, válvula 3/2, etc."
                  rows={2}
                  style={{ minHeight: 56, resize: "vertical" }}
                  required
                />
              </div>
              <div>
                <label className="pc-label">Quantidade</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="pc-input"
                  value={item.quantidade}
                  onChange={(e) =>
                    atualizarItem(index, "quantidade", e.target.value)
                  }
                  required
                />
              </div>
              <div>
                <label className="pc-label">Valor unitário</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="pc-input"
                  value={item.valorUnitario}
                  onChange={(e) =>
                    atualizarItem(index, "valorUnitario", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="pc-label">Valor total</label>
                <div className="pc-input" style={{ backgroundColor: "#020617" }}>
                  {calcularValorTotal(item).toFixed(2)}
                </div>
              </div>
            </div>

            {podeControleEstoque && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <span className="pc-label" style={{ marginBottom: 0 }}>
                  Controle de estoque?
                </span>
                <label style={{ fontSize: 12 }}>
                  <input
                    type="radio"
                    name={`controleEstoque_${index}`}
                    value="sim"
                    checked={item.controleEstoque === true}
                    onChange={() => atualizarItem(index, "controleEstoque", true)}
                  />{" "}
                  Sim
                </label>
                <label style={{ fontSize: 12 }}>
                  <input
                    type="radio"
                    name={`controleEstoque_${index}`}
                    value="nao"
                    checked={item.controleEstoque === false}
                    onChange={() => atualizarItem(index, "controleEstoque", false)}
                  />{" "}
                  Não
                </label>
              </div>
            )}

            {itens.length > 1 && (
              <div style={{ marginTop: 8, textAlign: "right" }}>
                <button
                  type="button"
                  className="pc-btn-link"
                  onClick={() => removerItem(index)}
                >
                  Remover
                </button>
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Ações */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        <button
          type="button"
          className="pc-btn-secundario"
          onClick={() => navigate(-1)}
          disabled={salvando}
        >
          Voltar
        </button>
        <button type="submit" className="pc-btn-primario" disabled={salvando}>
          {salvando ? "Salvando..." : "Salvar pedido"}
        </button>
      </div>
    </form>
  );

  const conteudo = (
    <div className="pc-page">
      <header className="pc-page-header">
        <div>
          <h1>{tituloPagina}</h1>
          <p className="pc-page-subtitle">
            Registre aqui os materiais solicitados {cabecalho.origem === "OS" ? "para a OS selecionada." : "para estoque ou uso geral."}
          </p>
        </div>
      </header>

      {erro && (
        <div className="pc-alert pc-alert-erro" style={{ marginBottom: 12 }}>
          {erro}
        </div>
      )}
      {sucesso && (
        <div className="pc-alert pc-alert-sucesso" style={{ marginBottom: 12 }}>
          {sucesso}
        </div>
      )}

      {carregando && <p>Carregando dados...</p>}
      {!carregando && conteudoForm}
    </div>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo={tituloPagina}
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <div className="m-form-container">
          {erro && <div className="m-alert m-alert-erro">{erro}</div>}
          {sucesso && <div className="m-alert m-alert-sucesso">{sucesso}</div>}

          {carregando ? (
            <p style={{ padding: 16, textAlign: "center" }}>Carregando...</p>
          ) : (
            <form onSubmit={handleSalvar}>
              {/* Dados do Pedido */}
              <div className="m-form-section">
                <h3 className="m-form-section-title">Dados do Pedido</h3>
                <div className="m-form-group">
                  <label className="m-form-label">Nº Requisição</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      className="m-form-input"
                      value={requisicaoNumero}
                      onChange={(e) => setRequisicaoNumero(e.target.value)}
                      placeholder="REQ-..."
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Cabeçalho - Dados da OS */}
              {cabecalho.origem === "OS" && (
                <div className="m-form-section">
                  <h3 className="m-form-section-title">Dados da OS</h3>
                  <div className="m-form-group">
                    <label className="m-form-label">Número da OS</label>
                    <div className="m-form-input-readonly">{cabecalho.osCodigo || "-"}</div>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Empresa</label>
                    <div className="m-form-input-readonly">{cabecalho.empresaNome || "-"}</div>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Setor</label>
                    <div className="m-form-input-readonly">{cabecalho.setorNome || "-"}</div>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Máquina</label>
                    <div className="m-form-input-readonly">{cabecalho.maquinaNome || "-"}</div>
                  </div>
                </div>
              )}

              {/* Materiais */}
              <div className="m-form-section">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <h3 className="m-form-section-title" style={{ margin: 0 }}>Materiais</h3>
                  <button type="button" className="m-btn-secondary-sm" onClick={adicionarItem}>
                    + Adicionar
                  </button>
                </div>

                {itens.map((item, index) => (
                  <div key={index} className="m-form-card">
                    <div className="m-form-group">
                      <label className="m-form-label">Modelo / Descrição</label>
                      <textarea
                        className="m-form-input"
                        value={item.modelo}
                        onChange={(e) => atualizarItem(index, "modelo", e.target.value)}
                        placeholder="Ex: Mangueira 1/4, válvula..."
                        rows={2}
                        style={{ minHeight: 56, resize: "vertical" }}
                        required
                      />
                    </div>

                    <div className="m-form-row">
                      <div className="m-form-group" style={{ flex: 1 }}>
                        <label className="m-form-label">Qtd</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="m-form-input"
                          value={item.quantidade}
                          onChange={(e) => atualizarItem(index, "quantidade", e.target.value)}
                          required
                        />
                      </div>
                      <div className="m-form-group" style={{ flex: 1 }}>
                        <label className="m-form-label">Valor Unit.</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="m-form-input"
                          value={item.valorUnitario}
                          onChange={(e) => atualizarItem(index, "valorUnitario", e.target.value)}
                        />
                      </div>
                      <div className="m-form-group" style={{ flex: 1 }}>
                        <label className="m-form-label">Total</label>
                        <div className="m-form-input-readonly">
                          R$ {calcularValorTotal(item).toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {podeControleEstoque && (
                      <div className="m-form-group">
                        <label className="m-form-label">Controle de estoque?</label>
                        <div className="m-form-radio-group">
                          <label className="m-form-radio">
                            <input
                              type="radio"
                              name={`controleEstoque_${index}`}
                              checked={item.controleEstoque === true}
                              onChange={() => atualizarItem(index, "controleEstoque", true)}
                            />
                            <span>Sim</span>
                          </label>
                          <label className="m-form-radio">
                            <input
                              type="radio"
                              name={`controleEstoque_${index}`}
                              checked={item.controleEstoque === false}
                              onChange={() => atualizarItem(index, "controleEstoque", false)}
                            />
                            <span>Não</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {itens.length > 1 && (
                      <button
                        type="button"
                        className="m-btn-link-danger"
                        onClick={() => removerItem(index)}
                      >
                        Remover item
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Ações */}
              <div className="m-form-actions">
                <button
                  type="button"
                  className="m-btn-secondary"
                  onClick={() => navigate(-1)}
                  disabled={salvando}
                >
                  Voltar
                </button>
                <button type="submit" className="m-btn-primary" disabled={salvando}>
                  {salvando ? "Salvando..." : "Salvar pedido"}
                </button>
              </div>
            </form>
          )}
        </div>
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo={tituloPagina} subtitulo="">
      {conteudo}
    </LayoutDesktop>
  );
}

export default PedidosMaterialPage;
